import React, { useState, useEffect } from "react";
import {
    Box, TextField, Button, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, CircularProgress, Stack
} from "@mui/material";
import { Print } from "@mui/icons-material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { format, getDaysInMonth, startOfMonth, endOfMonth } from "date-fns";

function Attendancereport() {
    const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [attendanceMap, setAttendanceMap] = useState({});
    
    // Store calculated status for each staff & date (e.g., 'Paid Holiday' vs 'Absent')
    const [computedMonthMap, setComputedMonthMap] = useState({});

    // Calculate days in the selected month
    const year = parseInt(selectedMonth.split("-")[0]);
    const month = parseInt(selectedMonth.split("-")[1]) - 1;
    const daysInMonth = getDaysInMonth(new Date(year, month));
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch staff (filter active staff only)
            const staffSnap = await getDocs(collection(db, "staff"));
            const activeStaff = staffSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(s => s.status === "Currently Working" || !s.status);
            setStaffList(activeStaff);

            // 2. Fetch attendance for selected month
            const start = format(startOfMonth(new Date(year, month)), "yyyy-MM-dd");
            const end = format(endOfMonth(new Date(year, month)), "yyyy-MM-dd");

            const q = query(
                collection(db, "staffattendance"),
                where("date", ">=", start),
                where("date", "<=", end)
            );

            const attSnap = await getDocs(q);
            const rawMap = {};
            attSnap.docs.forEach(doc => {
                const data = doc.data();
                const key = `${data.staffId}_${data.date}`;
                rawMap[key] = data.status;
            });
            setAttendanceMap(rawMap);

            // 3. Compute Paid Holidays vs Unpaid Absences per staff member
            const computedMap = {};

            activeStaff.forEach(staff => {
                const allowedHolidays = staff.paidHolidays !== undefined ? Number(staff.paidHolidays) : 3;
                let phUsed = 0;

                daysArray.forEach(d => {
                    const dateStr = `${selectedMonth}-${String(d).padStart(2, '0')}`;
                    const key = `${staff.id}_${dateStr}`;
                    const rawStatus = rawMap[key];

                    if (rawStatus === "Absent") {
                        if (phUsed < allowedHolidays) {
                            computedMap[key] = "Paid Holiday";
                            phUsed++;
                        } else {
                            computedMap[key] = "Absent";
                        }
                    } else if (rawStatus) {
                        computedMap[key] = rawStatus;
                    }
                });
            });

            setComputedMonthMap(computedMap);

        } catch (error) {
            console.error("Error fetching report data:", error);
        }
        setLoading(false);
    };

    useEffect(() => { 
        fetchData(); 
    }, [selectedMonth]); // Triggers auto-fetch when month changes

    const getStatusChar = (staffId, day) => {
        const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
        const status = computedMonthMap[`${staffId}_${dateStr}`];

        if (status === "Present") return <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>P</span>;
        if (status === "Paid Holiday") return <span style={{ color: '#1565c0', fontWeight: 'bold' }}>PH</span>;
        if (status === "Absent") return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>A</span>;
        if (status === "Half Day") return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>H</span>;
        return "-";
    };

    const calculateTotal = (staffId) => {
        let p = 0, h = 0, a = 0, ph = 0;

        daysArray.forEach(day => {
            const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
            const status = computedMonthMap[`${staffId}_${dateStr}`];

            if (status === "Present") p++;
            if (status === "Half Day") h++;
            if (status === "Paid Holiday") ph++;
            if (status === "Absent") a++;
        });

        // Payable Days = Present + Paid Holidays + Half Day (0.5)
        const total = p + ph + (h * 0.5);

        return { p, h, ph, a, total };
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Control Panel (Hidden during Print) */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }} className="no-print">
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Select Month" 
                        type="month" 
                        size="small"
                        value={selectedMonth} 
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={fetchData} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : "Load Report"}
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={() => window.print()} 
                        startIcon={<Print />}
                        disabled={loading || staffList.length === 0}
                    >
                        Print
                    </Button>
                </Stack>
            </Paper>

            {/* Attendance Report Sheet */}
            <TableContainer className="printable-area p-2" component={Paper} sx={{ borderRadius: 2, border: '1px solid #ccc' }}>
                <Box textAlign="center" p={2}>
                    <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: '700' }}>
                        Monthly Attendance Report - {format(new Date(year, month), "MMMM yyyy")}
                    </Typography>
                </Box>
                
                <Table size="small" sx={{ minWidth: 1000 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={headerCell} align="center">Sr.</TableCell>
                            <TableCell sx={{ ...headerCell, minWidth: 150, position: 'sticky', left: 0, zIndex: 10, bgcolor: '#f5f5f5' }}>
                                Staff Name
                            </TableCell>
                            {daysArray.map(d => (
                                <TableCell key={d} align="center" sx={dayCell}>{d}</TableCell>
                            ))}
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#e8f5e9', color: '#2e7d32' }}>P</TableCell>
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#fff3e0', color: '#ed6c02' }}>H</TableCell>
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#e3f2fd', color: '#1565c0' }}>PH</TableCell>
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#ffebee', color: '#d32f2f' }}>A</TableCell>
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#e3f2fd', color: '#1976d2' }}>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {staffList.map((staff, index) => {
                            const stats = calculateTotal(staff.id);
                            return (
                                <TableRow key={staff.id} hover>
                                    <TableCell align="center">{index + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 5, bgcolor: '#fff' }}>
                                        {staff.name}
                                    </TableCell>
                                    {daysArray.map(d => (
                                        <TableCell key={d} align="center" sx={dayCell}>
                                            {getStatusChar(staff.id, d)}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>{stats.p}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>{stats.h}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1565c0' }}>{stats.ph}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>{stats.a}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f0f7ff' }}>{stats.total}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dynamic CSS Styles for Print vs Screen Media Layouts */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .printable-area, .printable-area * { visibility: visible; }
                    .printable-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        border: none !important; 
                    }
                    .no-print { display: none !important; }
                    @page { size: A4 landscape; margin: 8mm; }
                }
            `}</style>
        </Box>
    );
}

const headerCell = { fontWeight: 'bold', border: '1px solid #ddd', fontSize: '0.75rem', p: 0.75 };
const dayCell = { border: '1px solid #ddd', p: 0.25, fontSize: '0.65rem', minWidth: '22px' };

export default Attendancereport;